import MenuItem, { ButtonContainer, LinkContainer } from "@components/MenuItem"
import VisualLink from "@components/VisualLink/Mobile"
import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import { getIn } from "@utils/index"
import { FilterFunnelProps, MenuDataProps } from "@utils/types"
import { getShopURLForDevice, getShopURLForDeviceType } from "@utils/urls"
import React from "react"
import styled from "styled-components"

import { ActionLink, BreadcrumbButton, Headers } from "./common"

const openProps = `
  transform: translateX(0);
  transition: transform ${animation.medium} ${cubicBezier};
`
const Container = styled.div<{ $isOpen?: boolean }>`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  transform: translateX(100%);
  transition: transform ${animation.medium} ${cubicBezier};
  will-change: transform;
  background-color: ${colors.offWhite};
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 3;
  cursor: default;
  ${({ $isOpen }) => ($isOpen ? openProps : "")}
`

const overlayActiveProps = `
  opacity: 0.1
  transition: opacity ${animation.medium} ${cubicBezier};
`
const Overlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  width: 100%;
  will-change: opacity;
  opacity: 0;
  transition: opacity ${animation.kindaLong} ${cubicBezier};
  background-color: ${colors.black};
  pointer-events: none;
  z-index: 2;
  ${({ $isVisible }) => ($isVisible ? overlayActiveProps : "")}
`

const Brands = styled.div`
  overflow: hidden;

  ${ButtonContainer} {
    color: ${colors.black};

    svg {
      stroke: ${colors.black};
    }
  }

  ${LinkContainer} {
    color: ${colors.black};

    svg {
      stroke: ${colors.black};
    }
  }
`

const VisualLinksContainer = styled.div`
  width: 100%;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
`

interface SelectBrandProps {
  data: MenuDataProps
  deviceTypeSlug?: string
  filterFunnel: FilterFunnelProps
  megaMenuLinks?: {
    external: boolean
    image: {
      file: {
        url: string
      }
    }
    link: string
    openNewTab: boolean
    text: string
  }[]
  onRequestClose?: () => void
  onSelectBrand?: (brand) => void
}

const SelectBrand: React.FC<SelectBrandProps> = ({
  data,
  deviceTypeSlug,
  filterFunnel,
  megaMenuLinks,
  onRequestClose,
  onSelectBrand,
}) => {
  const handleClose = () => {
    if (onRequestClose) {
      onRequestClose()
    }
  }

  const formatBrand = ({ brandName, brandSlug }) => {
    const thisFilterFunnel = getIn(
      filterFunnel,
      `brand.${deviceTypeSlug}.${brandSlug}`
    )

    if (thisFilterFunnel) {
      const link = getShopURLForDevice(thisFilterFunnel.slug)
      if (window && getIn(window, "location.pathname") !== link) {
        return {
          label: brandName,
          link,
        }
      } else {
        return {
          label: brandName,
          onClick: handleClose,
        }
      }
    }

    return {
      label: brandName,
      onClick: () => {
        if (onSelectBrand) {
          onSelectBrand(brandSlug)
        }
      },
    }
  }

  const deviceTypeData = (deviceTypeSlug && data[deviceTypeSlug]) || {}
  const brands = getIn(deviceTypeData, "brands", [])
  const formattedBrands = React.useMemo(
    () => brands.map(formatBrand),
    [deviceTypeData]
  )

  return (
    <>
      <Overlay $isVisible={!!deviceTypeSlug} />
      <Container $isOpen={!!deviceTypeSlug}>
        <Headers>
          <BreadcrumbButton
            label={getIn(deviceTypeData, "deviceType.label", "")}
            onClick={onRequestClose}
          />
          {deviceTypeSlug && (
            <ActionLink to={getShopURLForDeviceType(deviceTypeSlug)}>
              All
            </ActionLink>
          )}
        </Headers>
        <Brands>
          {formattedBrands.map((props) => (
            <MenuItem key={props.label} {...props} />
          ))}
        </Brands>
        {megaMenuLinks && (
          <VisualLinksContainer>
            {megaMenuLinks?.map(({ image, link, text }, idx: number) => (
              <VisualLink
                image={image}
                key={`${text} - ${idx}`}
                link={link}
                title={text}
              />
            ))}
          </VisualLinksContainer>
        )}
      </Container>
    </>
  )
}

export default SelectBrand
